import { forwardRef, useEffect, useId, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { Link } from "react-router-dom";
import { Icon, type IconName } from "./Icon.tsx";
import { useClickOutside } from "./hooks.ts";

/* ------------------------------------------------------------------ */
/* Buttons                                                             */
/* ------------------------------------------------------------------ */
export type ButtonVariant = "primary" | "outline" | "ghost" | "success" | "danger" | "danger-soft";
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "md" | "sm" | "xs";
  icon?: IconName;
  loading?: boolean;
  /** When set, render as a router link. */
  to?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({ variant = "outline", size = "md", icon, loading, to, className = "", children, type = "button", disabled, ...rest }, ref) {
  const cls = `btn btn-${variant}${size === "sm" ? " btn-sm" : size === "xs" ? " btn-xs" : ""}${loading ? " is-loading" : ""} ${className}`.trim();
  const inner = (
    <>
      {icon && <Icon name={icon} size={16} />}
      {children}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={cls} aria-disabled={disabled || undefined}>
        {inner}
      </Link>
    );
  }
  return (
    <button ref={ref} type={type} className={cls} disabled={disabled || loading} {...rest}>
      {inner}
    </button>
  );
});

export function IconButton({ icon, label, plain, pip, className = "", ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon: IconName; label: string; plain?: boolean; pip?: boolean }) {
  return (
    <button type="button" className={`icon-btn${plain ? " plain" : ""} ${className}`.trim()} aria-label={label} title={label} {...rest}>
      <Icon name={icon} size={plain ? 16 : 18} />
      {pip && <span className="pip" />}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Cards, badges, tags                                                 */
/* ------------------------------------------------------------------ */
export function Card({ className = "", children, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`card ${className}`.trim()} {...rest}>
      {children}
    </div>
  );
}
export function CardHead({ title, hint, actions, children }: { title: ReactNode; hint?: ReactNode; actions?: ReactNode; children?: ReactNode }) {
  return (
    <div className="card-head">
      <div className="flex wrap">
        <h3>{title}</h3>
        {hint && <span className="hint">{hint}</span>}
      </div>
      {(actions ?? children) && <div className="flex">{actions ?? children}</div>}
    </div>
  );
}
export function CardBody({ flush, className = "", children, ...rest }: React.HTMLAttributes<HTMLDivElement> & { flush?: boolean }) {
  return (
    <div className={`card-body${flush ? " flush" : ""} ${className}`.trim()} {...rest}>
      {children}
    </div>
  );
}

export type Tone = "success" | "warning" | "danger" | "info" | "neutral" | "primary";
export function Badge({ tone = "neutral", dot, children, className = "", title }: { tone?: Tone; dot?: boolean; children: ReactNode; className?: string; title?: string }) {
  return (
    <span className={`badge badge-${tone} ${className}`.trim()} title={title}>
      {dot && <span className="bdot" />}
      {children}
    </span>
  );
}
export function Tag({ children, warn, className = "" }: { children: ReactNode; warn?: boolean; className?: string }) {
  return <span className={`tag${warn ? " tag-warn" : ""} ${className}`.trim()}>{children}</span>;
}

/** Status pill: the word carries the meaning, the tone supports it. */
const STATUS_TONES: Record<string, Tone> = {
  approved: "success", active: "success", certified: "success", linked: "success", paid: "success", complete: "success", reviewed: "success", closed: "neutral", resolved: "success", succeeded: "success", done: "success",
  pending: "warning", under_review: "warning", expiring: "warning", onboarding: "warning", warned: "warning", sent: "info", filed: "warning", flagged: "warning", draft: "neutral", new: "warning", new_request: "warning", overdue: "danger", seasonal: "neutral",
  rejected: "danger", expired: "danger", terminated: "danger", suspended: "danger", failed: "danger", voided: "danger", critical: "danger", major: "danger", minor: "warning", inactive: "neutral", prospect: "info", per_location: "info", not_assessed: "neutral", not_approved: "danger", lapsed: "danger", ended: "neutral",
};
export function StatusPill({ status, label, tone }: { status: string | null | undefined; label?: string; tone?: Tone }) {
  if (!status) return <span className="faint">—</span>;
  const t = tone ?? STATUS_TONES[status] ?? "neutral";
  return (
    <Badge tone={t} dot>
      {label ?? status.replace(/_/g, " ")}
    </Badge>
  );
}

/* ------------------------------------------------------------------ */
/* Form controls                                                       */
/* ------------------------------------------------------------------ */
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { mono?: boolean; small?: boolean }>(function Input({ className = "", mono, small, ...rest }, ref) {
  return <input ref={ref} className={`input${mono ? " mono" : ""}${small ? " sm" : ""} ${className}`.trim()} {...rest} />;
});
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { small?: boolean }>(function Select({ className = "", small, children, ...rest }, ref) {
  return (
    <select ref={ref} className={`select${small ? " sm" : ""} ${className}`.trim()} {...rest}>
      {children}
    </select>
  );
});
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className = "", ...rest }, ref) {
  return <textarea ref={ref} className={`textarea ${className}`.trim()} {...rest} />;
});

export function FormField({ label, required, help, error, children, className = "", full, htmlFor }: { label: ReactNode; required?: boolean; help?: ReactNode; error?: ReactNode; children: ReactNode; className?: string; full?: boolean; htmlFor?: string }) {
  return (
    <div className={`field${error ? " has-error" : ""}${full ? " full" : ""} ${className}`.trim()}>
      <label htmlFor={htmlFor}>
        {label} {required && <span className="req">*</span>}
      </label>
      {children}
      {error ? <div className="error" role="alert">{error}</div> : help ? <div className="help">{help}</div> : null}
    </div>
  );
}

export function Switch({ checked, onChange, label, disabled, className = "", name }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean; className?: string; name?: string }) {
  return (
    <label className={`switch${disabled ? " disabled" : ""} ${className}`.trim()}>
      <input type="checkbox" name={name} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="track" />
      {label && <span>{label}</span>}
    </label>
  );
}

export function Segmented<T extends string>({ value, onChange, options, small, disabled, ariaLabel }: { value: T | null | undefined; onChange: (v: T) => void; options: Array<{ value: T; label: ReactNode }>; small?: boolean; disabled?: boolean; ariaLabel?: string }) {
  return (
    <div className={`segmented${small ? " sm" : ""}`} role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className={value === o.value ? "active" : ""} disabled={disabled} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function YesNo({ value, onChange, disabled }: { value: "yes" | "no" | null | undefined; onChange: (v: "yes" | "no") => void; disabled?: boolean }) {
  return <Segmented value={value ?? null} onChange={onChange} small disabled={disabled} options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} />;
}

export function CheckRow({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; disabled?: boolean }) {
  const id = useId();
  return (
    <div className="check-row">
      <input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <label htmlFor={id}>{label}</label>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tabs                                                                */
/* ------------------------------------------------------------------ */
export interface TabDef {
  key: string;
  label: ReactNode;
  count?: number | null;
}
export function Tabs({ tabs, value, onChange }: { tabs: TabDef[]; value: string; onChange: (k: string) => void }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={value === t.key} className={`tab${value === t.key ? " active" : ""}`} onClick={() => onChange(t.key)}>
          {t.label}
          {t.count !== undefined && t.count !== null && <span className="count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page & record furniture                                             */
/* ------------------------------------------------------------------ */
export function Breadcrumb({ items }: { items: Array<{ label: ReactNode; to?: string }> }) {
  return (
    <div className="breadcrumb">
      {items.map((it, i) => (
        <span key={i} className="flex" style={{ gap: 6 }}>
          {i > 0 && <Icon name="chev-right" size={14} />}
          {it.to ? <Link to={it.to}>{it.label}</Link> : <span>{it.label}</span>}
        </span>
      ))}
    </div>
  );
}

export function PageHeader({ crumbs, title, subtitle, actions, children }: { crumbs?: Array<{ label: ReactNode; to?: string }>; title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children?: ReactNode }) {
  useEffect(() => {
    if (typeof title === "string" && title.trim()) document.title = `${title} · CRC Kashrus`;
  }, [title]);
  return (
    <div className="page-head">
      <div style={{ flex: 1, minWidth: 0 }}>
        {crumbs && <Breadcrumb items={crumbs} />}
        <h1>{title}</h1>
        {subtitle && <p className="sub">{subtitle}</p>}
        {children}
      </div>
      {actions && <div className="head-actions">{actions}</div>}
    </div>
  );
}

export function RecordHeader({ avatar, title, meta, crumbs, actions }: { avatar: ReactNode; title: ReactNode; meta?: ReactNode; crumbs?: Array<{ label: ReactNode; to?: string }>; actions?: ReactNode }) {
  return (
    <div className="page-head">
      <div style={{ flex: 1, minWidth: 0 }}>
        {crumbs && <Breadcrumb items={crumbs} />}
        <div className="record-head mt-4">
          <div className="record-avatar">{avatar}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1>{title}</h1>
            {meta && <div className="meta-line">{meta}</div>}
          </div>
        </div>
      </div>
      {actions && <div className="head-actions">{actions}</div>}
    </div>
  );
}
export function Sep() {
  return <span className="sep" />;
}

export function MiniStat({ value, label, tone, to }: { value: ReactNode; label: ReactNode; tone?: "warning" | "info" | "danger" | "success"; to?: string }) {
  const style = tone ? { color: `var(--${tone})` } : undefined;
  const inner = (
    <>
      <div className="v" style={style}>
        {value}
      </div>
      <div className="k">{label}</div>
    </>
  );
  return to ? (
    <Link className="mini-stat-card" to={to}>
      {inner}
    </Link>
  ) : (
    <div className="mini-stat-card">{inner}</div>
  );
}

export function KpiCard({ tone, icon, label, value, delta, deltaTone = "flat", deltaIcon }: { tone: "indigo" | "green" | "amber" | "red" | "blue"; icon: IconName; label: ReactNode; value: ReactNode; delta?: ReactNode; deltaTone?: "up" | "down" | "flat"; deltaIcon?: IconName }) {
  return (
    <div className={`kpi tone-${tone}`}>
      <div className="kpi-icon">
        <Icon name={icon} />
      </div>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {delta && (
        <div className={`delta ${deltaTone}`}>
          {deltaIcon && <Icon name={deltaIcon} size={14} />} {delta}
        </div>
      )}
    </div>
  );
}

export function KV({ items, one }: { items: Array<{ k: ReactNode; v: ReactNode; empty?: boolean }>; one?: boolean }) {
  return (
    <div className={`kv${one ? " one" : ""}`}>
      {items.map((it, i) => (
        <div className="cell" key={i}>
          <div className="k">{it.k}</div>
          <div className={`v${it.empty || it.v === null || it.v === undefined || it.v === "" ? " empty" : ""}`}>{it.v === null || it.v === undefined || it.v === "" ? "Not set" : it.v}</div>
        </div>
      ))}
    </div>
  );
}

export function ListRow({ icon, iconTone = "primary", title, detail, right, when, to, onClick, compact }: { icon?: IconName; iconTone?: "primary" | "info" | "success" | "warning" | "danger"; title: ReactNode; detail?: ReactNode; right?: ReactNode; when?: ReactNode; to?: string; onClick?: () => void; compact?: boolean }) {
  const inner = (
    <>
      {icon && (
        <div className={`list-icon ${iconTone}`}>
          <Icon name={icon} size={18} />
        </div>
      )}
      <div className="list-main">
        <div className="t">{title}</div>
        {detail && <div className="d">{detail}</div>}
      </div>
      {right}
      {when && <span className="when">{when}</span>}
    </>
  );
  const cls = `list-row${compact ? " compact" : ""}`;
  if (to) {
    return (
      <Link className={cls} to={to}>
        {inner}
      </Link>
    );
  }
  if (onClick) {
    return (
      <div className={cls} role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => e.key === "Enter" && onClick()}>
        {inner}
      </div>
    );
  }
  return <div className={cls}>{inner}</div>;
}

export function Alert({ tone = "neutral", icon, children, className = "", action }: { tone?: "neutral" | "warn" | "info" | "danger" | "success"; icon?: IconName; children: ReactNode; className?: string; action?: ReactNode }) {
  const name = icon ?? (tone === "warn" || tone === "danger" ? "alert" : tone === "success" ? "check" : "info");
  return (
    <div className={`alert${tone === "neutral" ? "" : " " + tone} ${className}`.trim()} role={tone === "danger" ? "alert" : "status"}>
      <Icon name={name} />
      <div className="a-body">{children}</div>
      {action}
    </div>
  );
}

/** Warnings live under the header, not in a dialog — the flag-don't-block surface. */
export function Banners({ items }: { items: Array<{ tone: "warning" | "info" | "danger" | "success"; text: ReactNode; action?: ReactNode }> }) {
  if (!items.length) return null;
  return (
    <div className="banners">
      {items.map((b, i) => (
        <Alert key={i} tone={b.tone === "warning" ? "warn" : b.tone} action={b.action}>
          {b.text}
        </Alert>
      ))}
    </div>
  );
}

export function Callout({ icon = "info", children }: { icon?: IconName; children: ReactNode }) {
  return (
    <div className="callout">
      <Icon name={icon} size={16} />
      <div>{children}</div>
    </div>
  );
}

export function SectionHead({ n, title, right }: { n?: number | string; title: ReactNode; right?: ReactNode }) {
  return (
    <div className="form-section-head">
      {n !== undefined && <span className="num">{n}</span>}
      <h3>{title}</h3>
      {right && <span className="right">{right}</span>}
    </div>
  );
}

export function Avatar({ name, size = 36 }: { name: string | null | undefined; size?: number }) {
  const parts = (name ?? "?").replace(/^(rabbi|r\.)\s+/i, "").split(/\s+/).filter(Boolean);
  const text = (parts.length >= 2 ? `${parts[0]![0]}${parts[parts.length - 1]![0]}` : (parts[0] ?? "?").slice(0, 2)).toUpperCase();
  return (
    <div className="avatar" style={{ width: size, height: size, fontSize: size * 0.36 }} aria-hidden="true">
      {text}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Skeleton / states                                                   */
/* ------------------------------------------------------------------ */
export function Skeleton({ w = "100%", h = 12, className = "", style }: { w?: number | string; h?: number; className?: string; style?: React.CSSProperties }) {
  return <div className={`skeleton ${className}`.trim()} style={{ width: w, height: h, ...style }} aria-hidden="true" />;
}
export function SkeletonRows({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="skeleton-rows" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, r) => (
        <div className="skeleton-row" key={r}>
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} w={c === 0 ? "30%" : `${12 + ((r * 7 + c * 11) % 18)}%`} h={12} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ icon = "list", title, text, actions, error }: { icon?: IconName; title: ReactNode; text?: ReactNode; actions?: ReactNode; error?: boolean }) {
  return (
    <div className={`state${error ? " error" : ""}`} role={error ? "alert" : undefined}>
      <div className="st-icon">
        <Icon name={error ? "alert" : icon} />
      </div>
      <h4>{title}</h4>
      {text && <p>{text}</p>}
      {actions && <div className="st-actions">{actions}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Dropdown menu (row actions, user menu)                              */
/* ------------------------------------------------------------------ */
export interface MenuItemDef {
  label: ReactNode;
  icon?: IconName;
  onClick?: () => void;
  to?: string;
  danger?: boolean;
  disabled?: boolean;
  /** shown as a note under a disabled item: why you cannot do it */
  reason?: string;
  sep?: boolean;
}
export function Menu({ items, trigger, align = "right", label = "Actions" }: { items: MenuItemDef[]; trigger?: ReactNode; align?: "left" | "right"; label?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));
  return (
    <div className="menu-wrap" ref={ref}>
      <span onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} onKeyDown={(e) => e.key === "Escape" && setOpen(false)}>
        {trigger ?? <IconButton icon="dots" label={label} plain aria-haspopup="menu" aria-expanded={open} />}
      </span>
      {open && (
        <div className={`menu${align === "left" ? " left" : ""}`} role="menu">
          {items.map((it, i) =>
            it.sep ? (
              <div className="menu-sep" key={i} />
            ) : it.to && !it.disabled ? (
              <Link key={i} className={`menu-item${it.danger ? " danger" : ""}`} to={it.to} role="menuitem" onClick={() => setOpen(false)}>
                {it.icon && <Icon name={it.icon} />}
                {it.label}
              </Link>
            ) : (
              <div key={i}>
                <button
                  type="button"
                  className={`menu-item${it.danger ? " danger" : ""}`}
                  role="menuitem"
                  disabled={it.disabled}
                  title={it.disabled ? it.reason : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    it.onClick?.();
                  }}
                >
                  {it.icon && <Icon name={it.icon} />}
                  {it.label}
                </button>
                {it.disabled && it.reason && <div className="menu-note">{it.reason}</div>}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Dropzone                                                            */
/* ------------------------------------------------------------------ */
export function Dropzone({ onFiles, accept, hint, title = "Attach a file", multiple, disabled }: { onFiles: (files: File[]) => void; accept?: string; hint?: ReactNode; title?: ReactNode; multiple?: boolean; disabled?: boolean }) {
  const [over, setOver] = useState(false);
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={`dropzone${over ? " over" : ""}`}
      style={disabled ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (!disabled) onFiles(Array.from(e.dataTransfer.files));
      }}
    >
      <input id={id} type="file" className="sr-only" accept={accept} multiple={multiple} disabled={disabled} onChange={(e) => onFiles(Array.from(e.target.files ?? []))} />
      <div className="dz-icon">
        <Icon name="clip" />
      </div>
      <div>
        <b>{title}</b>
      </div>
      <div style={{ fontSize: 12, marginTop: 3 }}>{hint ?? "Drag & drop a PDF or photo, or click to browse · up to 25 MB"}</div>
    </label>
  );
}

/* ------------------------------------------------------------------ */
/* Keyset pager                                                        */
/* ------------------------------------------------------------------ */
export function Pager({ shown, total, hasMore, onMore, loadingMore, label = "rows" }: { shown: number; total?: number; hasMore: boolean; onMore: () => void; loadingMore?: boolean; label?: string }) {
  return (
    <div className="pagination">
      <span>
        Showing <b>{shown.toLocaleString()}</b>
        {total !== undefined && (
          <>
            {" "}
            of <b>{total.toLocaleString()}</b>
          </>
        )}{" "}
        {label}
      </span>
      <div className="pager">
        <button type="button" disabled={!hasMore || loadingMore} onClick={onMore}>
          {loadingMore ? "Loading…" : hasMore ? "Load more ›" : "All loaded"}
        </button>
      </div>
    </div>
  );
}
